import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { Guest } from "../api/contracts/reservation";
import { useBookingStore } from "../stores/bookingStore";
import type { Intent, StepKey } from "./engine/types";

export type DisplayStepKey = StepKey | "pick";

/** sessionStorage key scoped to the site path segment, the way the booking-code store is: one
 *  origin serves every site, and a plan must not ride along into the next site in the tab. */
export const bookingFlowStorageKey = (
  pathname: string = window.location.pathname,
): string => `booking-flow:${pathname.split("/")[1] ?? ""}`;

export type PlanUnit = { id: string; guests: Guest[] };

export type BookingFlowPlan = {
  planId: string;
  siteId: string;
  units: PlanUnit[];
  intents: Record<string, Intent>;
  activeUnitId: string;
  confirmedGuestUnits?: string[];
  stepOrders?: Record<string, DisplayStepKey[]>;
};

interface BookingFlowState {
  plan: BookingFlowPlan | null;
  setPlan: (plan: BookingFlowPlan) => void;
  clear: () => void;
  hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

/**
 * The units whose rows the backend holds — bookings, not search drafts. What the header's
 * guest card leaves alone: it edits the units still to book, and a search puts those back
 * beside these untouched.
 */
export const bookedUnitIds = (plan: BookingFlowPlan | null): Set<string> =>
  new Set(
    Object.entries(plan?.intents ?? {})
      .filter(([, i]) => (i.rowIds?.length ?? 0) > 0 || !!i.receiptPending)
      .map(([id]) => id),
  );

export const unitCapReached = (unitCount: number, maxUnits: number | undefined): boolean =>
  typeof maxUnits === "number" && unitCount >= maxUnits;

export const newPlanId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `plan_${Date.now()}_${Math.random().toString(16).slice(2)}`;

export const useBookingFlowStore = create<BookingFlowState>()(
  persist(
    (set) => ({
      plan: null,
      setPlan: (plan) => set({ plan }),
      clear: () => {
        set({ plan: null });
        useBookingFlowStore.persist.clearStorage();
      },
      hasHydrated: false,
      setHasHydrated: (state) => set({ hasHydrated: state }),
    }),
    {
      name: bookingFlowStorageKey(),
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ plan: state.plan }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    },
  ),
);

// The plan dies with the cart. Subscribed at module level, not in the provider: the key is reset
// from the cart and the confirmation page too, where no booking-flow component is mounted.
useBookingStore.subscribe((state, previous) => {
  if (previous.reservationKey && !state.reservationKey) {
    useBookingFlowStore.getState().clear();
  }
});
